import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

interface StatsProps {
  t: any;
  isRTL: boolean;
}

const StatsSection = styled.section`
  padding: 6rem 2rem;
  background: #f8f9fa;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="stats-grid" width="10" height="10" patternUnits="userSpaceOnUse"><circle cx="5" cy="5" r="1" fill="#dc2626" opacity="0.05"/></pattern></defs><rect width="100" height="100" fill="url(%23stats-grid)"/></svg>');
    opacity: 0.5;
  }
  
  @media (max-width: 768px) {
    padding: 4rem 1.5rem;
  }
`;

const Container = styled.div<{ $isRTL: boolean }>`
  max-width: 1400px;
  margin: 0 auto;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
  position: relative;
  z-index: 1;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 3rem;
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 2.5rem;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
    max-width: 350px;
    margin: 0 auto;
  }
`;

const StatCard = styled(motion.div)`
  text-align: center;
  padding: 2rem 1.5rem;
  background: white;
  backdrop-filter: blur(10px);
  border-radius: 20px;
  border: 2px solid rgba(220, 38, 38, 0.1);
  transition: all 0.4s ease;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  
  &:hover {
    background: #f8fafc;
    border-color: rgba(220, 38, 38, 0.3);
    transform: translateY(-10px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  }
  
  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
    border-radius: 20px;
    border: 2px solid rgba(220, 38, 38, 0.15);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    
    &:hover {
      transform: translateY(-8px);
      box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
    }
  }
`;

const StatNumber = styled(motion.div)<{ $isRTL: boolean }>`
  font-size: 3.5rem;
  font-weight: 800;
  margin-bottom: 0.5rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  text-shadow: none;
  line-height: 1;
  
  background: linear-gradient(135deg, #000000 0%, #dc2626 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  
  @media (max-width: 768px) {
    font-size: 3rem;
    margin-bottom: 0.5rem;
  }
`;

const StatLabel = styled.p<{ $isRTL: boolean }>`
  font-size: 1.1rem;
  color: #64748b;
  font-weight: 500;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  line-height: 1.4;
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    line-height: 1.4;
  }
`;

const Stats: React.FC<StatsProps> = ({ t, isRTL }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.5, // Trigger when 50% of the section is visible
  });

  const [counts, setCounts] = useState({
    experience: 0,
    projects: 0,
    partners: 0,
  });

  const stats = [
    { 
      id: 'experience',
      target: 17, 
      suffix: '+', 
      label: isRTL ? 'سنوات خبرة' : 'Ans d\'expérience',
      duration: 2500
    },
    { 
      id: 'projects',
      target: 100, 
      suffix: '+', 
      label: isRTL ? 'مشاريع' : 'Projets',
      duration: 3000
    },
    { 
      id: 'partners',
      target: 70, 
      suffix: '+', 
      label: isRTL ? 'شركاء' : 'Partenaires',
      duration: 2500
    },
  ];

  useEffect(() => {
    if (!inView) return;

    stats.forEach((stat) => {
      let startTime: number;
      const duration = stat.duration;
      
      const animate = (currentTime: number) => {
        if (!startTime) startTime = currentTime;
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        // Easing function for smooth, readable counting
        const easeOutCubic = 1 - Math.pow(1 - progress, 3);
        const currentValue = Math.floor(easeOutCubic * stat.target);
        
        setCounts(prev => ({
          ...prev,
          [stat.id]: currentValue
        }));
        
        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          setCounts(prev => ({
            ...prev,
            [stat.id]: stat.target
          }));
        }
      };
      
      requestAnimationFrame(animate);
    });
  }, [inView]);

  return (
    <StatsSection id="stats" ref={ref}>
      <Container $isRTL={isRTL}>
        <StatsGrid>
          {stats.map((stat, index) => (
            <StatCard
              key={stat.id}
              initial={{ opacity: 0, y: 50 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ 
                duration: 0.6, 
                delay: index * 0.1 
              }}
            >
              <StatNumber $isRTL={isRTL}>
                {counts[stat.id as keyof typeof counts]}{stat.suffix}
              </StatNumber>
              <StatLabel $isRTL={isRTL}>{stat.label}</StatLabel>
            </StatCard>
          ))}
        </StatsGrid>
      </Container>
    </StatsSection>
  );
};

export default Stats; 